import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Modal as AntModal, Input, Form, Button } from 'antd';
import { Modal } from '../../design-system/components/feedback/Modal';
import { CreateTableModal } from './CreateTableModal';
import { 
  ViewGridIcon,
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TrashIcon,
  PencilIcon,
  LockClosedIcon,
  CheckIcon,
  XIcon,
  MailIcon,
  SearchIcon,
  ChartBarIcon,
  CogIcon,
  ChatIcon,
  TagIcon,
  TruckIcon
} from '@heroicons/react/outline';
import styled, { createGlobalStyle } from 'styled-components';
import { ResearchTable } from './ResearchTable';
import { researchTableService, type ResearchTable as ResearchTableType } from '../../services/researchTableService';
import { resourcesService } from '../../services/resourcesService';
import { projectsService, type Project } from '../../services/projectsService';
import { ColumnDropdown } from '../../common/ColumnDropdown/ColumnDropdown';
import { TABLE_TEMPLATES, convertTemplateColumnsToBackend } from './tableTemplates';
import { type TableTemplate } from './types';
import { TableType, TABLE_TYPES } from '../../types/tableTypes';
import { colors, spacing, radius, typography } from '../../design-system';
import { ProjectOverlay } from '../../common/ProjectOverlay';
import { PlatformIcon, OutlookIcon } from '../../design-system/components/data-display/EmailProviderIcons';
import { instagramProspectingTemplate, tiktokProspectingTemplate } from './templates/prospecting';
import { trackOutreachTemplate } from './templates/outreach';
import { collectShippingTemplate } from './templates/logistics';

// Styled components for workspace layout
const WorkspaceContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  max-width: 100%;
  width: 100%;
  min-width: 0;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  max-width: 100%;
  min-width: 0;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  min-height: 0;
  max-width: 100%;
  width: 100%;
  min-width: 0;
`;

const MainContentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  background: white;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
`;

const HeaderViewModeToggle = styled.div`
  display: flex;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 6px;
  padding: 2px;
  gap: 2px;
`;

const HeaderViewModeButton = styled.button<{ $active: boolean }>`
  padding: 4px 12px;
  border: none;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.$active ? `
    background: white;
    color: ${colors.text.primary};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  ` : `
    background: transparent;
    color: ${colors.text.secondary};
    
    &:hover {
      color: ${colors.text.primary};
      background: rgba(255, 255, 255, 0.5);
    }
  `}
`;

const MainContentBody = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const TablesBar = styled.div`
  height: 36px;
  background: #fafbfc;
  border: none;
  border-radius: 8px 8px 0 0;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  overflow: visible;
  position: relative;
  margin: 0 8px;
  box-shadow: 0 -1px 2px 0 rgba(0, 0, 0, 0.04);
  z-index: 100;
`;

// Toggle button removed - bottom bar is always visible like Excel sheets

// Header removed - not needed for Excel-style tabs

// Removed unused sidebar title components

const SidebarContent = styled.div`
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0 12px;
  display: flex;
  align-items: center;
  
  /* Subtle scrollbar */
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.15) transparent;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.25);
  }
`;

const TablesList = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
`;

const TableItem = styled.div<{ $active?: boolean; $isDragging?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: grab;
  transition: all 0.15s ease;
  background: ${props => {
    if (props.$isDragging) return '#e5e7eb';
    return props.$active ? 'rgba(0, 0, 0, 0.04)' : 'transparent';
  }};
  color: ${props => props.$active ? colors.text.primary : colors.text.tertiary};
  font-size: 12px;
  font-weight: ${props => props.$active ? '500' : '400'};
  position: relative;
  opacity: ${props => props.$isDragging ? 0.5 : 1};
  white-space: nowrap;
  border: 1px solid transparent;
  min-width: 100px;
  max-width: 180px;
  
  &:hover {
    background: ${props => {
      if (props.$isDragging) return '#e5e7eb';
      return props.$active ? 'rgba(0, 0, 0, 0.06)' : 'rgba(0, 0, 0, 0.02)';
    }};
    color: ${colors.text.primary};
  }
  
  &:active {
    cursor: grabbing;
  }
`;

const TableActions = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
`;

const TableName = styled.span`
  opacity: 1;
  visibility: visible;
  font-size: 12px;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SidebarFooter = styled.div`
  margin-left: auto;
  padding: 4px 12px;
  border-left: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: flex-end;
  flex-shrink: 0;
  padding-bottom: 4px;
`;

const AddTableButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  color: #9ca3af;
  background: transparent;
  border: none;
  margin-right: 8px;
  
  &:hover {
    background: rgba(0, 0, 0, 0.04);
    color: #6b7280;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

// Empty state components
const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 40px 0;
  max-width: 900px;
  margin: 0 auto;
  height: 100%;
  overflow-y: auto;
`;

const EmptyStateTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 6px 0;
`;

const EmptyStateDescription = styled.p`
  font-size: 13px;
  color: ${colors.text.secondary};
  margin: 0 0 32px 0;
  text-align: center;
`;

const EmptyStateTemplatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  width: 100%;
  max-width: 900px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const EmptyStateTemplateCard = styled.button<{ $isCustom?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
  transition: border-color 0.15s ease;
  background: ${props => props.$isCustom ? '#2d3748' : '#f8f9fa'};
  border: ${props => props.$isCustom ? '1px solid #2d3748' : '1px solid transparent'};
  cursor: pointer;
  text-align: left;
  
  &:hover {
    border-color: ${props => props.$isCustom ? '#1a202c' : 'rgba(0, 0, 0, 0.06)'};
  }
  
  ${props => props.$isCustom && `
    grid-column: 1 / -1;
    max-width: 450px;
    justify-self: center;
  `}
`;

const EmptyStateTemplateInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const EmptyStateTemplateHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const EmptyStateTemplateName = styled.div<{ $isCustom?: boolean }>`
  font-weight: 500;
  color: ${props => props.$isCustom ? 'rgba(255, 255, 255, 0.95)' : '#374151'};
  font-size: 12px;
  line-height: 1.2;
`;

const EmptyStateTemplateDescription = styled.div<{ $isCustom?: boolean }>`
  font-size: 11px;
  color: ${props => props.$isCustom ? 'rgba(255, 255, 255, 0.7)' : colors.text.tertiary};
  line-height: 1.5;
  font-weight: 400;
  opacity: 0.8;
`;

const EmptyStateTemplateColumns = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const EmptyStateColumnIcon = styled.div<{ $isCustom?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background: ${props => props.$isCustom ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.6)'};
  border-radius: 3px;
  flex-shrink: 0;
  
  svg, img {
    width: 10px;
    height: 10px;
    opacity: 0.7;
  }
`;

const EmptyStateColumnBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 6px;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 3px;
  font-size: 10px;
  color: ${colors.text.secondary};
  font-weight: 400;
  
  svg, img {
    width: 9px;
    height: 9px;
    flex-shrink: 0;
    opacity: 0.7;
  }
`;

// Template selection styled components - enhanced design from GlobalOverviewPage
const TemplateSections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-bottom: 32px;
`;

const TemplateSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 4px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0;
  letter-spacing: -0.01em;
`;

const SectionDivider = styled.div`
  flex: 1;
  height: 1px;
  background: linear-gradient(to right, #e5e7eb, transparent);
  margin-left: 12px;
`;

const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (min-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const TemplateCard = styled.button<{ $selected: boolean; $variant?: string; $locked?: boolean }>`
  background: ${props => props.$selected ? '#495057' : '#ffffff'};
  border: 2px solid ${props => {
    if (props.$selected) return '#495057';
    if (props.$locked) return '#e9ecef';
    return '#e9ecef';
  }};
  border-radius: 16px;
  padding: 0;
  cursor: ${props => props.$locked ? 'default' : 'pointer'};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  min-height: 160px;
  position: relative;
  overflow: hidden;
  box-shadow: ${props => props.$selected ? 
    '0 8px 25px -5px rgba(73, 80, 87, 0.25), 0 4px 10px -2px rgba(73, 80, 87, 0.1)' : 
    '0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.03)'
  };
  opacity: ${props => props.$locked ? 0.5 : 1};
  transform: ${props => props.$selected ? 'translateY(-4px)' : 'translateY(0)'};
  
  &:hover {
    border-color: ${props => {
      if (props.$locked) return '#e9ecef';
      if (props.$selected) return '#343a40';
      return '#6c757d';
    }};
    box-shadow: ${props => {
      if (props.$locked) return '0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.03)';
      if (props.$selected) return '0 12px 35px -5px rgba(73, 80, 87, 0.3), 0 6px 15px -2px rgba(73, 80, 87, 0.15)';
      return '0 4px 15px -1px rgba(0, 0, 0, 0.1), 0 2px 8px -1px rgba(0, 0, 0, 0.06)';
    }};
    transform: ${props => {
      if (props.$locked) return 'translateY(0)';
      if (props.$selected) return 'translateY(-6px)';
      return 'translateY(-2px)';
    }};
    background: ${props => {
      if (props.$selected) return '#343a40';
      return '#f8f9fa';
    }};
  }
`;

const TemplateCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 16px 20px;
  width: 100%;
`;

const TemplateCardHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TemplateIcon = styled.div<{ $selected: boolean; $variant?: string }>`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$selected ? '#ffffff' : '#6c757d'};
  flex-shrink: 0;
  transition: color 0.2s ease;
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const TemplateHeaderTitle = styled.h3<{ $variant?: string }>`
  font-size: 10px;
  font-weight: 500;
  color: #adb5bd;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const TemplateCardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  text-align: left;
  padding: 0 20px 20px 20px;
  gap: 8px;
`;

const TemplateName = styled.div<{ $selected: boolean; $variant?: string }>`
  font-size: 15px;
  font-weight: 600;
  color: ${props => props.$selected ? '#ffffff' : '#495057'};
  line-height: 1.2;
  margin: 0;
  letter-spacing: '-0.01em';
  transition: color 0.2s ease;
`;

const TemplateDescription = styled.div<{ $variant?: string; $selected?: boolean }>`
  font-size: 12px;
  color: ${props => props.$selected ? 'rgba(255, 255, 255, 0.8)' : '#6c757d'};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
  margin: 0;
  transition: color 0.2s ease;
`;

const TemplateBottomRight = styled.div`
  position: absolute;
  bottom: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TemplateLockIcon = styled.div<{ $variant?: string; $selected?: boolean }>`
  width: 16px;
  height: 16px;
  color: ${props => props.$selected ? 'rgba(255, 255, 255, 0.7)' : '#adb5bd'};
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const TemplateIntegrationBadge = styled.div<{ $selected?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: ${props => props.$selected ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.9)'};
  border: 1px solid ${props => props.$selected ? 'rgba(255, 255, 255, 0.2)' : '#e9ecef'};
  border-radius: 8px;
  padding: 4px 8px;
  font-size: 9px;
  color: ${props => props.$selected ? 'rgba(255, 255, 255, 0.8)' : '#6c757d'};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
`;

const TemplateDataSourceBadges = styled.div`
  display: flex;
  gap: 4px;
`;

const TemplateDataSourceBadge = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$selected ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.9)'};
  border: 1px solid ${props => props.$selected ? 'rgba(255, 255, 255, 0.2)' : '#e9ecef'};
  border-radius: 6px;
  padding: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  width: 18px;
  height: 18px;
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

// Modal button styles
const ModalButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing[2]} ${spacing[3]};
  border-radius: ${radius.sm};
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
  cursor: pointer;
  min-width: 80px;
  
  ${props => props.$variant === 'primary' ? `
    background: ${colors.brand.primary};
    color: white;
    border: none;
    
    &:hover {
      background: ${colors.brand.primaryHover};
      transform: translateY(-1px);
    }
    
    &:active {
      transform: translateY(0);
    }
  ` : `
    background: transparent;
    color: ${colors.text.tertiary};
    border: 1px solid ${colors.border.secondary};
    
    &:hover {
      background: rgba(255, 255, 255, 0.15);
      border-color: ${colors.text.tertiary};
    }
    
    &:active {
      background: rgba(255, 255, 255, 0.25);
    }
  `}
`;

// CreatorModal-style components for stage selection
const StageModalStyles = createGlobalStyle`
  .stage-modal {
    background: ${colors.palette.gray[100]} !important;
    top: 2% !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    height: 96% !important;
    max-height: 96% !important;
    
    /* Remove default modal body padding */
    & > div:last-child {
      padding: 0 !important;
    }
  }
`;

const StageModalContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StageTopPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing[2]} ${spacing[4]};
  background: ${colors.palette.gray[25]};
  border-bottom: 1px solid rgba(0, 0, 0, 0.02);
`;

const StageTitleSection = styled.div`
  flex: 1;
  max-width: 300px;
  
  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.text.primary};
  }
`;

const StageContentArea = styled.div`
  flex: 1;
  background: ${colors.palette.gray[100]};
  padding: ${spacing[2]} ${spacing[8]};
  display: flex;
  justify-content: center;
  overflow-y: auto;
`;

const StageContentSheet = styled.div`
  width: 100%;
  max-width: 1200px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-height: 600px;
  display: flex;
  flex-direction: column;
  padding: ${spacing[6]};
`;

const StageActionButtons = styled.div`
  display: flex;
  gap: ${spacing[1]};
  align-items: center;
`;

const StageSaveButton = styled.button`
  background: transparent;
  color: ${colors.text.primary};
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  line-height: 1;
  
  &:hover:not(:disabled) {
    background: ${colors.background.hover};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

const StageSecondaryButton = styled.button`
  background: transparent;
  color: ${colors.text.secondary};
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  line-height: 1;
  
  &:hover:not(:disabled) {
    background: ${colors.background.hover};
    color: ${colors.text.primary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[8]};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const StageSectionHeader = styled.div`
  margin-bottom: ${spacing[4]};
`;

const StageSectionTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding-bottom: ${spacing[2]};
  border-bottom: 1px solid ${colors.border.secondary};
`;

const StageTemplateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

const StageTemplateCard = styled.button<{ $selected: boolean }>`
  background: ${props => props.$selected ? colors.palette.gray[50] : '#ffffff'};
  border: 1px solid ${props => props.$selected ? colors.palette.indigo[300] : colors.border.secondary};
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 12px;
  
  &:hover {
    border-color: ${colors.palette.indigo[300]};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }
  
  ${props => props.$selected && `
    box-shadow: 0 0 0 3px ${colors.palette.indigo[100]};
  `}
`;

const StageTemplateIcon = styled.div`
  width: 32px;
  height: 32px;
  background: ${colors.palette.gray[100]};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.secondary};
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const StageTemplateName = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.text.primary};
`;

const StageTemplateDescription = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${colors.text.secondary};
  line-height: 1.4;
`;

interface ResearchTablesV2Props {
  libraryExtensionExpanded?: boolean;
  exportTrigger?: number;
  copyTrigger?: number;
  refreshTrigger?: number;
  runAllTrigger?: number;
  runUnrunTrigger?: number;
  analyzeOutreachTrigger?: { templateId: string; timestamp: number } | null;
}

// Helper function to organize templates by sections
const organizeTemplatesBySection = (templates: any[]) => {
  const sections = [
    {
      title: 'Prospecting & Research',
      templates: templates.filter(t => 
        t.id.includes('prospecting') || t.id === 'tiktok_prospecting' || t.id === 'instagram_prospecting'
      )
    },
    {
      title: 'Outreach & Communication', 
      templates: templates.filter(t => 
        t.id === 'invitation' || t.id === 'negotiation'
      )
    },
    {
      title: 'Collect Creator Info',
      templates: templates.filter(t => 
        t.id === 'collect_shipping_info' || t.id === 'collect_payment_info_b2b' || t.id === 'collect_payment_info_b2c'
      )
    },
    {
      title: 'Generate & Send Links/Codes',
      templates: templates.filter(t => 
        t.id === 'generate_bitly_links' || t.id === 'generate_shopify_codes' || t.id === 'shopify_promo_code'
      )
    },
    {
      title: 'Content Verification',
      templates: templates.filter(t => 
        t.id === 'content_verification'
      )
    },
    {
      title: 'Track Posts & Performance',
      templates: templates.filter(t => 
        t.id === 'track_tiktok_posts' || t.id === 'track_instagram_posts'
      )
    },
    {
      title: 'Payment Processing',
      templates: templates.filter(t => 
        t.id === 'process_payments'
      )
    },
    {
      title: 'Other Templates',
      templates: templates.filter(t => 
        !t.id.includes('prospecting') && t.id !== 'invitation' && t.id !== 'negotiation' && 
        t.id !== 'tiktok_prospecting' && t.id !== 'instagram_prospecting' &&
        t.id !== 'collect_shipping_info' && t.id !== 'collect_payment_info_b2b' && t.id !== 'collect_payment_info_b2c' &&
        t.id !== 'content_verification' && t.id !== 'track_tiktok_posts' && t.id !== 'track_instagram_posts' &&
        t.id !== 'generate_bitly_links' && t.id !== 'generate_shopify_codes' && t.id !== 'shopify_promo_code' &&
        t.id !== 'process_payments'
      )
    }
  ].filter(section => section.templates.length > 0); // Only show sections with templates
  
  return sections;
};

// Generate abbreviation from table name (first letter of each word, up to 2 letters)
const getTableAbbreviation = (name: string): string => {
  const words = name.trim().split(/\s+/);
  if (words.length === 0) return '';
  
  // Take first letter of first two words (or just first word if only one)
  const letters = words
    .slice(0, 2)
    .map(word => word.charAt(0).toUpperCase())
    .join('');
  
  return letters;
};

export const ResearchTablesV2: React.FC<ResearchTablesV2Props> = ({ 
  libraryExtensionExpanded = false, 
  exportTrigger = 0,
  copyTrigger = 0,
  refreshTrigger = 0,
  runAllTrigger = 0,
  runUnrunTrigger = 0,
  analyzeOutreachTrigger = null
}) => {
  const { projectId } = useParams<{ projectId: string }>();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [tableToDelete, setTableToDelete] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [tableToEdit, setTableToEdit] = useState<{ id: string; name: string } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Use project context
  // Project context is now URL-driven, no manual setting needed
  
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [allTables, setAllTables] = useState<ResearchTableType[]>([]);
  const [loading, setLoading] = useState(false);
  
  // We're always in project workspace mode now
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(projectId || null);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [activeTableId, setActiveTableId] = useState<string | null>(null);
  const [projectTables, setProjectTables] = useState<ResearchTableType[]>([]);

  // Selection state
  const [selectedTables, setSelectedTables] = useState<string[]>([]);
  
  // Drag & drop state
  const [draggedTableId, setDraggedTableId] = useState<string | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  
  // Create modals state
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [showCreateTableModal, setShowCreateTableModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('prospect_research');
  const [projectForm] = Form.useForm();
  const [tableForm] = Form.useForm();
  
  // Project templates for requirements validation
  const [projectTemplates, setProjectTemplates] = useState<string[]>([]);
  
  // Project overlay state for empty state
  const [showProjectOverlay, setShowProjectOverlay] = useState(false);
  
  // Connection status
  const [hasOutlookConnected, setHasOutlookConnected] = useState(false);
  const [hasShopifyConnected, setHasShopifyConnected] = useState(false);
  
  // Load connection status
  useEffect(() => {
    const loadConnectionStatus = async () => {
      try {
        const { channelService } = await import('../../services/channelService');
        const channels = await channelService.getAllChannels();
        
        const hasOutlook = Array.isArray(channels) && channels.some((channel: any) => channel.channel_type === 'email');
        const hasShopify = Array.isArray(channels) && channels.some((channel: any) => channel.channel_type === 'shopify' || channel.type === 'shopify');
        
        setHasOutlookConnected(hasOutlook);
        setHasShopifyConnected(hasShopify);
      } catch (error) {
        console.error('Error loading connection status:', error);
      }
    };
    
    loadConnectionStatus();
  }, []);

  // Handle URL params and initial load
  useEffect(() => {
    if (projectId) {
      // URL has projectId, load project data
      setSelectedProjectId(projectId);
      fetchProject(projectId);
      fetchProjects(); // Load all projects for dropdown
    } else {
      // No projectId in URL, redirect to projects list
      navigate('/projects');
    }
  }, [projectId, navigate]);

  useEffect(() => {
    if (selectedProjectId) {
      fetchProjectTables(selectedProjectId);
    }
  }, [selectedProjectId]);

  // Handle table selection from URL query parameter
  useEffect(() => {
    const tableParam = searchParams.get('table');
    if (tableParam && projectTables.length > 0) {
      // Check if the table ID from URL exists in the project's tables
      const tableExists = projectTables.find(table => table.id === tableParam);
      if (tableExists) {
        console.log('🎯 [ResearchTablesV2] Setting active table from URL:', tableParam);
        setActiveTableId(tableParam);
        return;
      }
    }
    
    // Auto-select first table when project tables are loaded and no specific table requested
    if (projectTables.length > 0 && !activeTableId && !tableParam) {
      console.log('🎯 [ResearchTablesV2] Auto-selecting first table:', projectTables[0].id);
      setActiveTableId(projectTables[0].id);
    }
  }, [projectTables, activeTableId, searchParams]);

  // Handle create table modal trigger from URL query parameter
  useEffect(() => {
    const createParam = searchParams.get('create');
    if (createParam === 'true' && selectedProjectId) {
      console.log('🚀 [ResearchTablesV2] Opening create table modal from URL parameter');
      setSelectedTemplate('prospect_research');
      setShowCreateTableModal(true);
      
      // Clear the create parameter from URL to avoid reopening modal on refresh
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('create');
      const newUrl = `${location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`;
      navigate(newUrl, { replace: true });
    }
  }, [searchParams, selectedProjectId, navigate, location.pathname]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      console.log('🔍 [ResearchTablesV2] Starting to fetch projects...');
      const projects = await projectsService.getAllProjects();
      console.log('📋 [ResearchTablesV2] Fetched projects:', projects);
      console.log('📊 [ResearchTablesV2] Number of projects:', projects?.length || 0);
      setAllProjects(projects || []);
    } catch (error) {
      console.error('❌ [ResearchTablesV2] Error fetching projects:', error);
      setAllProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProject = async (projectId: string) => {
    try {
      console.log('📋 [ResearchTablesV2] Fetching project:', projectId);
      const project = await projectsService.getProject(projectId);
      console.log('✅ [ResearchTablesV2] Loaded project:', project.name);
      setCurrentProject(project);
      // Global context is now URL-driven, no manual setting needed
    } catch (error) {
      console.error('❌ [ResearchTablesV2] Error fetching project:', error);
      // If project not found, redirect to projects list
      navigate('/projects');
    }
  };

  const fetchProjectTables = async (projectId: string) => {
    setLoading(true);
    try {
      const tables = await researchTableService.getResearchTables(projectId);
      console.log('📋 Fetched project tables:', tables);
      setProjectTables(tables || []);
    } catch (error) {
      console.error('❌ Error fetching project tables:', error);
      setProjectTables([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch project templates for requirements validation
  const fetchProjectTemplates = async () => {
    if (!selectedProjectId) {
      setProjectTemplates([]);
      return;
    }
    
    try {
      const allResources = await resourcesService.getResources();
      // Filter resources that are associated with the current project and extract titles
      const templateTitles = allResources
        .filter(resource => 
          resource.associated_projects?.some(proj => proj.project_uuid === selectedProjectId)
        )
        .map(resource => resource.title);
      
      setProjectTemplates(templateTitles);
      console.log('📋 [ResearchTablesV2] Loaded project templates:', templateTitles);
    } catch (error) {
      console.error('❌ [ResearchTablesV2] Error fetching project templates:', error);
      setProjectTemplates([]);
    }
  };

  // Fetch project templates when project changes
  useEffect(() => {
    if (selectedProjectId) {
      fetchProjectTemplates();
    }
  }, [selectedProjectId]);


  const handleCreateTable = () => {
    console.log('🚀 [ResearchTablesV2] handleCreateTable called');
    console.log('📋 [ResearchTablesV2] selectedProjectId:', selectedProjectId);
    setSelectedTemplate('prospect_research');
    setShowCreateTableModal(true);
    console.log('✅ [ResearchTablesV2] Modal should now be open');
  };

  // Create table directly from template
  const handleCreateFromTemplate = async (template: TableTemplate, defaultName: string) => {
    try {
      if (!selectedProjectId) {
        console.error('No project selected');
        return;
      }

      console.log('🚀 [ResearchTablesV2] Creating table from template:', template.name);
      
      // Convert template columns to backend format
      const backendColumns = convertTemplateColumnsToBackend(template.defaultConfig?.columns || []);
      
      const defaultTableConfig: any = {
        columns: backendColumns,
        context_required: false
      };

      const newTable = await researchTableService.createResearchTable({
        name: defaultName,
        project_id: selectedProjectId,
        table_type: template.id as any,
        table_config: defaultTableConfig
      });

      console.log('✅ [ResearchTablesV2] Table created:', newTable);
      
      // Refresh tables and navigate to new table
      await fetchProjectTables(selectedProjectId);
      setActiveTableId(newTable.id);
      navigate(`/projects/${selectedProjectId}?table=${newTable.id}`);
    } catch (error) {
      console.error('Error creating table from template:', error);
    }
  };

  const handleCreateTableSubmit = async (data: { name: string; description?: string; table_type: TableType; context_required?: boolean }) => {
    try {
      console.log('🚀 [ResearchTablesV2] handleCreateTableSubmit called');
      console.log('📋 [ResearchTablesV2] Creating table with project_id:', selectedProjectId);
      console.log('📋 [ResearchTablesV2] Table type:', data.table_type);
      
      // Check if it's a workflow template from TABLE_TEMPLATES
      const workflowTemplate = TABLE_TEMPLATES.find(t => t.id === data.table_type);
      let defaultTableConfig: any = undefined;
      
      if (workflowTemplate?.defaultConfig?.columns) {
        // Convert unified template columns to backend format
        const backendColumns = convertTemplateColumnsToBackend(workflowTemplate.defaultConfig.columns);
        defaultTableConfig = {
          columns: backendColumns,
          context_required: data.context_required || false
        };
        
        console.log('📋 [ResearchTablesV2] Using workflow template columns (converted to backend format):', workflowTemplate.name);
      } else {
        // Check if it's a TABLE_TYPE (like Custom)
        const typeInfo = TABLE_TYPES[data.table_type];
        
        if (typeInfo?.suggestedColumns && typeInfo.suggestedColumns.length > 0) {
          // Convert suggested columns to actual table columns
          const columns = typeInfo.suggestedColumns.map((suggestedCol, index) => {
            const column: any = {
              id: `col_${index + 1}`,
              name: suggestedCol.name,
              cell_type: suggestedCol.type === 'platform_data' ? 'normal' : 
                         suggestedCol.type === 'communication' ? 'communication' :
                         suggestedCol.type === 'status' ? 'normal' : 
                         suggestedCol.requirement_config ? 'requirement' : 'normal',
              data_type: suggestedCol.type === 'number' ? 'number' : 'text',
              width: 150
            };

            // Add requirement config if present
            if (suggestedCol.requirement_config) {
              column.requirement_config = suggestedCol.requirement_config;
            }

            // Only add platform_resources if it exists and is not empty
            if (suggestedCol.platform_resources && suggestedCol.platform_resources.length > 0) {
              // Convert string array to objects with platform property
              column.platform_resources = suggestedCol.platform_resources.map(platform => ({
                platform: platform
              }));
            }

            return column;
          });
          
          defaultTableConfig = {
            columns: columns,
            context_required: data.context_required || false
          };
          
          console.log('📋 [ResearchTablesV2] Generated columns from table type:', typeInfo.name);
        } else {
          // No columns defined, just set context_required
          defaultTableConfig = {
            context_required: data.context_required || false
          };
        }
      }
      
      const newTable = await researchTableService.createResearchTable({
        name: data.name,
        description: data.description || '',
        project_id: selectedProjectId || undefined,
        table_type: data.table_type,
        table_config: defaultTableConfig
      });
      
      console.log('✅ [ResearchTablesV2] Table created successfully:', newTable);
      
      if (selectedProjectId) {
        console.log('🔄 [ResearchTablesV2] Refreshing project tables...');
        await fetchProjectTables(selectedProjectId);
      }
      
      setActiveTableId(newTable.id); // Switch to the new table tab
      
      // Update URL to show the new table
      if (selectedProjectId) {
        navigate(`/projects/${selectedProjectId}?table=${newTable.id}`, { replace: true });
      }
      
      setShowCreateTableModal(false);
      
      console.log('🎯 [ResearchTablesV2] Navigation to table view completed');
    } catch (error) {
      console.error('❌ [ResearchTablesV2] Error creating table:', error);
    }
  };

  const handleCreateTableCancel = () => {
    console.log('❌ [ResearchTablesV2] handleCreateTableCancel called');
    setShowCreateTableModal(false);
    setSelectedTemplate('prospect_research');
  };

  const handleTabClick = (tableId: string) => {
    setActiveTableId(tableId);
    // Update URL with table parameter
    if (selectedProjectId) {
      navigate(`/projects/${selectedProjectId}?table=${tableId}`, { replace: true });
    }
  };

  const handleBackToProjects = () => {
    navigate('/overview');
  };

  const handleProjectUpdated = async (updatedProject: Project) => {
    // Reload project data
    await fetchProject(updatedProject.project_uuid);
    console.log('[ResearchTablesV2] Project updated:', updatedProject);
  };

  const handleProjectDeleted = async (deletedProjectId: string) => {
    // Navigate back to overview since project is deleted
    navigate('/');
    console.log('[ResearchTablesV2] Project deleted:', deletedProjectId);
  };

  const handleDeleteTable = async (tableId: string) => {
    try {
      // Check if this is a system table
      const tableToDelete = projectTables.find(t => t.id === tableId);
      if (tableToDelete?.is_system_table) {
        console.warn('[ResearchTablesV2] Cannot delete system table:', tableToDelete.name);
        // Optionally show a notification to the user
        alert('This is a system table and cannot be deleted. It is automatically managed by the workflow system.');
        return;
      }
      
      await researchTableService.deleteResearchTable(tableId);
      
      // If we deleted the active table, switch to another tab or clear active table
      if (activeTableId === tableId) {
        const remainingTables = projectTables.filter(t => t.id !== tableId);
        setActiveTableId(remainingTables.length > 0 ? remainingTables[0].id : null);
      }
      
      if (selectedProjectId) {
        await fetchProjectTables(selectedProjectId);
      }
    } catch (error) {
      console.error('Error deleting table:', error);
    }
  };

  // Drag & drop handlers
  const handleDragStart = (e: React.DragEvent, tableId: string) => {
    setDraggedTableId(tableId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', tableId);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (!draggedTableId) return;
    
    const draggedIndex = projectTables.findIndex(t => t.id === draggedTableId);
    if (draggedIndex === -1 || draggedIndex === dropIndex) {
      setDraggedTableId(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder tables locally first for immediate feedback
    const reorderedTables = [...projectTables];
    const [draggedTable] = reorderedTables.splice(draggedIndex, 1);
    reorderedTables.splice(dropIndex, 0, draggedTable);
    
    // Update local state immediately
    setProjectTables(reorderedTables);
    
    // Prepare API payload with new workflow_order values
    const tableOrders = reorderedTables.map((table, index) => ({
      tableId: table.id,
      workflowOrder: index + 1
    }));

    try {
      // Update backend
      await researchTableService.reorderTables(tableOrders);
      console.log('✅ Tables reordered successfully');
    } catch (error) {
      console.error('❌ Failed to reorder tables:', error);
      // Revert local state on error
      if (selectedProjectId) {
        await fetchProjectTables(selectedProjectId);
      }
    }

    setDraggedTableId(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedTableId(null);
    setDragOverIndex(null);
  };

  console.log('📊 [ResearchTablesV2] Rendering project-workspace view, activeTableId:', activeTableId);
  return (
      <div style={{ 
        height: '100%', 
        background: 'white', 
        display: 'flex', 
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        padding: '0',
        minWidth: 0
      }}>

        {/* Main Content + Tab Bar */}
        <WorkspaceContainer>
          <ContentWrapper>
            <MainContent>
              {/* Main Content Body - Header moved to ProjectHeader */}
              <MainContentBody>
                {/* Main Content Area */}
                {loading ? (
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6b7280'
              }}>
                Loading tables...
              </div>
            ) : (!projectTables || projectTables.length === 0) ? (
              <EmptyStateContainer>
                <EmptyStateTitle>Create your first table</EmptyStateTitle>
                <EmptyStateDescription>
                  Tables help you organize and automate your workflow. Choose a template to get started.
                </EmptyStateDescription>
                <EmptyStateTemplatesGrid>
                  {/* Instagram Prospecting */}
                  <EmptyStateTemplateCard onClick={() => handleCreateFromTemplate(instagramProspectingTemplate, 'Instagram Prospecting')}>
                    <EmptyStateTemplateInfo>
                      <EmptyStateTemplateHeader>
                        <EmptyStateTemplateName>Instagram Prospecting</EmptyStateTemplateName>
                        <EmptyStateTemplateColumns>
                          <EmptyStateColumnIcon>
                            <PlatformIcon platform="instagram" size={10} />
                          </EmptyStateColumnIcon>
                        </EmptyStateTemplateColumns>
                      </EmptyStateTemplateHeader>
                      <EmptyStateTemplateDescription>
                        Research and analyze Instagram creators for your campaigns
                      </EmptyStateTemplateDescription>
                      <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {instagramProspectingTemplate.defaultConfig?.columns.slice(0, 4).map((col, idx) => (
                          <EmptyStateColumnBadge key={idx}>
                            <PlatformIcon platform="instagram" size={9} />
                            {col.name}
                          </EmptyStateColumnBadge>
                        ))}
                      </div>
                    </EmptyStateTemplateInfo>
                  </EmptyStateTemplateCard>

                  {/* TikTok Prospecting */}
                  <EmptyStateTemplateCard onClick={() => handleCreateFromTemplate(tiktokProspectingTemplate, 'TikTok Prospecting')}>
                    <EmptyStateTemplateInfo>
                      <EmptyStateTemplateHeader>
                        <EmptyStateTemplateName>TikTok Prospecting</EmptyStateTemplateName>
                        <EmptyStateTemplateColumns>
                          <EmptyStateColumnIcon>
                            <PlatformIcon platform="tiktok" size={10} />
                          </EmptyStateColumnIcon>
                        </EmptyStateTemplateColumns>
                      </EmptyStateTemplateHeader>
                      <EmptyStateTemplateDescription>
                        Research and analyze TikTok creators for your campaigns
                      </EmptyStateTemplateDescription>
                      <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {tiktokProspectingTemplate.defaultConfig?.columns.slice(0, 4).map((col, idx) => (
                          <EmptyStateColumnBadge key={idx}>
                            <PlatformIcon platform="tiktok" size={9} />
                            {col.name}
                          </EmptyStateColumnBadge>
                        ))}
                      </div>
                    </EmptyStateTemplateInfo>
                  </EmptyStateTemplateCard>

                  {/* Conversation Tracking */}
                  <EmptyStateTemplateCard onClick={() => handleCreateFromTemplate(trackOutreachTemplate, 'Conversation Tracking')}>
                    <EmptyStateTemplateInfo>
                      <EmptyStateTemplateHeader>
                        <EmptyStateTemplateName>Conversation Tracking</EmptyStateTemplateName>
                        <EmptyStateTemplateColumns>
                          <EmptyStateColumnIcon>
                            <OutlookIcon size={10} />
                          </EmptyStateColumnIcon>
                        </EmptyStateTemplateColumns>
                      </EmptyStateTemplateHeader>
                      <EmptyStateTemplateDescription>
                        Track outreach status and monitor email conversations with creators
                      </EmptyStateTemplateDescription>
                      <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {trackOutreachTemplate.defaultConfig?.columns.map((col, idx) => (
                          <EmptyStateColumnBadge key={idx}>
                            <OutlookIcon size={9} />
                            {col.name}
                          </EmptyStateColumnBadge>
                        ))}
                      </div>
                    </EmptyStateTemplateInfo>
                  </EmptyStateTemplateCard>

                  {/* Collect Shipping Info */}
                  <EmptyStateTemplateCard onClick={() => handleCreateFromTemplate(collectShippingTemplate, 'Collect Shipping Info')}>
                    <EmptyStateTemplateInfo>
                      <EmptyStateTemplateHeader>
                        <EmptyStateTemplateName>Collect Shipping Info</EmptyStateTemplateName>
                        <EmptyStateTemplateColumns>
                          <EmptyStateColumnIcon>
                            <OutlookIcon size={10} />
                          </EmptyStateColumnIcon>
                        </EmptyStateTemplateColumns>
                      </EmptyStateTemplateHeader>
                      <EmptyStateTemplateDescription>
                        Request and collect shipping addresses from creators for product seeding
                      </EmptyStateTemplateDescription>
                      <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {collectShippingTemplate.defaultConfig?.columns.slice(0, 4).map((col, idx) => (
                          <EmptyStateColumnBadge key={idx}>
                            <OutlookIcon size={9} />
                            {col.name}
                          </EmptyStateColumnBadge>
                        ))}
                        {collectShippingTemplate.defaultConfig && collectShippingTemplate.defaultConfig.columns.length > 4 && (
                          <EmptyStateColumnBadge>
                            +{collectShippingTemplate.defaultConfig.columns.length - 4} more
                          </EmptyStateColumnBadge>
                        )}
                      </div>
                    </EmptyStateTemplateInfo>
                  </EmptyStateTemplateCard>

                  {/* Custom */}
                  <EmptyStateTemplateCard onClick={handleCreateTable} $isCustom>
                    <EmptyStateTemplateInfo>
                      <EmptyStateTemplateHeader>
                        <EmptyStateTemplateName $isCustom>Custom Table</EmptyStateTemplateName>
                        <EmptyStateTemplateColumns>
                          <EmptyStateColumnIcon $isCustom>
                            <PlusIcon />
                          </EmptyStateColumnIcon>
                        </EmptyStateTemplateColumns>
                      </EmptyStateTemplateHeader>
                      <EmptyStateTemplateDescription $isCustom>
                        Build your own table from scratch with any columns you need
                      </EmptyStateTemplateDescription>
                    </EmptyStateTemplateInfo>
                  </EmptyStateTemplateCard>
                </EmptyStateTemplatesGrid>
              </EmptyStateContainer>
            ) : activeTableId ? (
              <>
                {console.log('🎯 [ResearchTablesV2] Rendering ResearchTable with activeTableId:', activeTableId, 'type:', typeof activeTableId)}
                <ResearchTable 
                  tableId={activeTableId}
                  projectId={selectedProjectId || undefined}
                // Don't show back button since we're in tabbed interface
                onBack={undefined}
                // Tab bar props
                projectTables={projectTables}
                activeTableId={activeTableId}
                onTabClick={handleTabClick}
                onDeleteTable={handleDeleteTable}
                onCreateTable={handleCreateTable}
                showTabBar={true}
                // Action triggers
                exportTrigger={exportTrigger}
                copyTrigger={copyTrigger}
                refreshTrigger={refreshTrigger}
                runAllTrigger={runAllTrigger}
                runUnrunTrigger={runUnrunTrigger}
                analyzeOutreachTrigger={analyzeOutreachTrigger}
                // Connection status
                hasOutlookConnected={hasOutlookConnected}
                hasShopifyConnected={hasShopifyConnected}
              />
              </>
            ) : (
              <ProjectOverlay
                isOpen={true}
                project={currentProject}
                onClose={() => {}} // Empty function since this is used as empty state
                onSuccess={handleProjectUpdated}
                onDelete={currentProject ? handleProjectDeleted : undefined}
                isEmptyState={true}
              />
            )}
              </MainContentBody>
            </MainContent>

            {/* Bottom Tab Bar (Excel-style sheet selector) */}
            <TablesBar>
              <SidebarContent>
                <TablesList>
                  <AddTableButton onClick={handleCreateTable}>
                    <PlusIcon />
                  </AddTableButton>
                  
                  {!loading && projectTables.map((table, index) => {
                    const isDragging = draggedTableId === table.id;
                    const isDropTarget = dragOverIndex === index;
                    
                    return (
                      <TableItem 
                        key={table.id} 
                        $active={activeTableId === table.id}
                        $isDragging={isDragging}
                        draggable={true}
                        onClick={() => handleTabClick(table.id)}
                        onDragStart={(e) => handleDragStart(e, table.id)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        style={{
                          borderLeft: isDropTarget ? '2px solid #635bff' : 'none',
                          marginLeft: isDropTarget ? '2px' : '0'
                        }}
                      >
                        <TableName>
                          {table.name}
                          {table.is_system_table && (
                            <LockClosedIcon style={{ width: 12, height: 12, marginLeft: 6, opacity: 0.5 }} />
                          )}
                        </TableName>
                        <TableActions onClick={(e) => e.stopPropagation()}>
                          <ColumnDropdown
                            sections={[{
                              options: [
                                // Only show Rename for non-system tables
                                ...(!table.is_system_table ? [{
                                  label: 'Rename',
                                  icon: <PencilIcon style={{ width: 14, height: 14 }} />,
                                  onClick: () => {
                                    setTableToEdit({ id: table.id, name: table.name });
                                    setEditOpen(true);
                                  }
                                }] : []),
                                // Only show Delete for non-system tables
                                ...(!table.is_system_table ? [{
                                  label: 'Delete',
                                  icon: <TrashIcon style={{ width: 14, height: 14 }} />,
                                  variant: 'danger' as const,
                                  onClick: () => {
                                    setTableToDelete(table.id);
                                    setDeleteOpen(true);
                                  }
                                }] : []),
                                // Show info for system tables
                                ...(table.is_system_table ? [{
                                  label: 'System Table',
                                  icon: <LockClosedIcon style={{ width: 14, height: 14 }} />,
                                  onClick: () => {
                                    alert('This table is automatically managed by the workflow system and cannot be deleted or renamed.');
                                  },
                                  disabled: true
                                }] : [])
                              ]
                            }]}
                            width={180}
                            position="right"
                            placement="top"
                          />
                        </TableActions>
                      </TableItem>
                    );
                  })}
                </TablesList>
              </SidebarContent>
            </TablesBar>
          </ContentWrapper>
      </WorkspaceContainer>

        {/* Create Table Modal - New Table Types System */}
        <CreateTableModal
          isOpen={showCreateTableModal}
          onClose={handleCreateTableCancel}
          onCreate={handleCreateTableSubmit}
          existingTemplates={projectTemplates}
        />
        {/* Rename Table Modal (simple prompt) */}
        <AntModal
          title="Rename Table"
          open={editOpen}
          onCancel={() => setEditOpen(false)}
          onOk={async () => {
            if (!tableToEdit) { setEditOpen(false); return; }
            try {
              const input = document.getElementById('rename-table-input') as HTMLInputElement | null;
              const newName = input?.value?.trim();
              if (!newName) { setEditOpen(false); return; }
              await researchTableService.updateResearchTable(tableToEdit.id, { name: newName });
              if (selectedProjectId) {
                await fetchProjectTables(selectedProjectId);
              }
            } finally {
              setEditOpen(false);
              setTableToEdit(null);
            }
          }}
          okText="Save"
          cancelText="Cancel"
          width={420}
          styles={{
            mask: {
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              backdropFilter: 'blur(4px)'
            },
            content: {
              padding: 0,
              borderRadius: '8px',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)'
            },
            header: {
              padding: '20px 24px',
              borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
              marginBottom: 0
            },
            body: {
              padding: '24px'
            },
            footer: {
              padding: '16px 24px',
              borderTop: '1px solid rgba(0, 0, 0, 0.04)',
              marginTop: 0
            }
          }}
        >
          <Input id="rename-table-input" defaultValue={tableToEdit?.name} placeholder="Enter table name" />
        </AntModal>

        {/* Delete confirmation */}
        <AntModal
          title="Delete Table"
          open={deleteOpen}
          onOk={async () => {
            if (!tableToDelete) return;
            try {
              await researchTableService.deleteResearchTable(tableToDelete);
              if (selectedProjectId) {
                await fetchProjectTables(selectedProjectId);
              }
            } catch (e) {
              console.error('Failed to delete table', e);
            } finally {
              setDeleteOpen(false);
              setTableToDelete(null);
            }
          }}
          onCancel={() => setDeleteOpen(false)}
          okText="Delete"
          cancelText="Cancel"
          okButtonProps={{ danger: true }}
        >
          <p>Are you sure you want to delete this table? This cannot be undone.</p>
        </AntModal>
      </div>
    );
};
